import {
    AfterViewInit,
    Component,
    ElementRef,
    forwardRef,
    Input,
    OnChanges,
    OnDestroy,
    OnInit,
    SimpleChanges,
    ViewChild
} from '@angular/core';
import {ControlValueAccessor, NG_VALUE_ACCESSOR} from '@angular/forms';
import {NzDatePickerComponent} from 'ng-zorro-antd/date-picker';
import {Subscription} from 'rxjs';
import {DateTime} from 'luxon';
import {CustomFormControl} from '../custom-form-control';
import {EMPTY_FUNCTION} from '../../../../helpers/empty.helper';
import {DATE_FORMAT} from '../../../../helpers/date.helper';

@Component({
    selector: 'app-datepicker',
    templateUrl: './datepicker.component.html',
    styleUrls: ['./datepicker.component.less'],
    providers: [
        {provide: NG_VALUE_ACCESSOR, useExisting: forwardRef(() => DatepickerComponent), multi: true},
    ]
})
export class DatepickerComponent extends CustomFormControl implements ControlValueAccessor, OnInit, OnChanges, OnDestroy, AfterViewInit {
    @Input() placeholder = 'LABELS.SELECT_PROMPT';
    @Input() size: string;
    @Input() clearable = true;
    @Input() min: string;
    @Input() max: string;
    @Input() minToday = false;
    @Input() maxToday = false;
    @Input() textOnDisabled = false;
    @Input() textAlign: 'center' | 'left' | 'right' = 'center';
    @Input() defaultDate = null;

    @ViewChild('nzDatepicker', {static: false}) nzDatepicker: NzDatePickerComponent;
    @ViewChild('datepickerInput', {static: false}) datepickerInput: ElementRef;
    disabledDateFn;

    nzDatepickerValue = null;

    subscriptions: Subscription = new Subscription();

    _onChange: (value: any) => void = EMPTY_FUNCTION;
    _onTouched = EMPTY_FUNCTION;

    ngOnInit(): void {
        this.control.valueChanges.subscribe(value => {
            this._onChange(value);
        });
        this.setDisabledDateFunction();
    }

    ngOnChanges(changes: SimpleChanges) {
        const todayDt = DateTime.local();
        if (this.minToday) {
            const minDt = this.minDt
                ? this.minDt < todayDt
                    ? todayDt
                    : this.minDt
                : todayDt;
            this.min = minDt.toFormat(DATE_FORMAT);
        }
        if (this.maxToday) {
            const maxDt = this.maxDt
                ? this.maxDt > todayDt
                    ? todayDt
                    : this.maxDt
                : todayDt;
            this.max = maxDt.toFormat(DATE_FORMAT);
        }
        if (changes.min || changes.max || changes.minToday || changes.maxToday) {
            this.setDisabledDateFunction();
        }
    }

    ngAfterViewInit(): void {
        if (this.nzDatepicker) {
            this.subscriptions.add(
                this.nzDatepicker.picker.focusChange.subscribe(() => {
                    this.datepickerInput.nativeElement.focus();
                })
            );
        }
    }

    ngOnDestroy() {
        this.subscriptions.unsubscribe();
    }

    writeValue(value) {
        this.control.setValue(value);
    }

    registerOnChange(fn: () => {}): void {
        this._onChange = fn;
    }

    registerOnTouched(fn: () => {}): void {
        this._onTouched = fn;
    }

    handleClear($event) {
        $event.stopPropagation();
        this.nzDatepickerValue = null;
        this.writeValue('');
    }

    handleDatepickerChange(value) {
        this.writeValue(DateTime.fromJSDate(value).toFormat(DATE_FORMAT));
    }

    handleControlChange(value) {
        if (this.isValidDate(value)) {
            this.nzDatepickerValue = new Date(value);
        }
    }

    handleBlur($event) {
        if (this.isValidDate($event?.target?.value)) {
            this.nzDatepickerValue = new Date($event?.target?.value);
            this.rangeCheck(this.nzDatepickerValue);
        } else {
            this.nzDatepickerValue = null;
            this.writeValue('');
        }
    }

    handleInputGroupClick($event) {
        $event.stopPropagation();
        this.nzDatepicker.open();
    }

    get minDt() {
        return this.min ? DateTime.fromFormat(this.min, DATE_FORMAT) : undefined;
    }

    get maxDt() {
        return this.max ? DateTime.fromFormat(this.max, DATE_FORMAT) : undefined;
    }

    rangeCheck(value: Date) {
        const calendarDate = DateTime.fromJSDate(value);
        if (this.minDt && this.minDt.isValid && calendarDate.toMillis() < this.minDt.toMillis()) {
            this.writeValue(this.minDt.toFormat(DATE_FORMAT));
        }
        if (this.maxDt && this.maxDt.isValid && calendarDate.toMillis() > this.maxDt.toMillis()) {
            this.writeValue(this.maxDt.toFormat(DATE_FORMAT));
        }
    }

    setDisabledDateFunction() {
        this.disabledDateFn = (value: Date) => {
            const calendarDate = DateTime.fromJSDate(value).startOf('day');
            if (!this.minDt && !this.maxDt) {
                return false;
            }
            if ((!this.minDt || !this.minDt.isValid) && (!this.maxDt && !this.maxDt.isValid)) {
                return false;
            } else if (this.minDt && this.minDt.isValid && this.maxDt && this.maxDt.isValid) {
                return calendarDate.toMillis() < this.minDt.toMillis() || calendarDate.toMillis() > this.maxDt.toMillis();
            } else if (this.minDt && this.minDt.isValid) {
                return calendarDate.toMillis() < this.minDt.toMillis();
            } else if (this.maxDt && this.maxDt.isValid) {
                return calendarDate.toMillis() > this.maxDt.toMillis();
            }
            return false;
        };
    }

    isValidDate(value: string) {
        return value && DateTime.fromFormat(value, DATE_FORMAT).isValid;
    }
}
